import { create } from "zustand";
import { getPickDetails } from "@/features/pickems/util/helpers";
import type { PickslipPick } from "../types/PickslipPick";
import type { Nullable } from "types/utilities";

type PickslipStore = {
  picks: PickslipPick[];
  confirmContestId: Nullable<string>;
  isLoading: boolean;
  pickAcceptedId: Nullable<string>;
  error: Nullable<string>;
  justAccepted: boolean;
};

type PickslipActions = {
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: Nullable<string>) => void;
  acceptPick: (pick: PickslipPick) => void;
  togglePick: (pick: PickslipPick) => void;
  removePick: (marketId: string) => void;
  updatePickslip: (pickslip: Partial<PickslipStore>) => void;
  reset: () => void;
};

type PickslipState = PickslipStore & PickslipActions;

const initialState: PickslipStore = {
  picks: [],
  confirmContestId: null,
  isLoading: false,
  pickAcceptedId: null,
  error: null,
  justAccepted: false,
};

const usePickslipStore = create<PickslipState>()((set) => ({
  ...initialState,

  setIsLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  acceptPick: (pick) =>
    set((state) => {
      const updatedPicks = state.picks.map((existingPick) => {
        if (existingPick.marketId === pick.marketId) {
          return {
            ...existingPick,
            accepted: true,
            odds: pick.freshOdds,
            lockedOdds: pick.freshOdds,
            freshOdds: pick.freshOdds,
            oddsChanged: false,
          };
        }
        return existingPick;
      });

      return {
        picks: updatedPicks,
        pickAcceptedId: null,
      };
    }),

  togglePick: (pick) =>
    set((state) => {
      const pickDetails = getPickDetails(pick, pick.contest);

      const isExistingPick = state.picks.some(
        (existingPick) =>
          existingPick.sourceId === pick.sourceId &&
          existingPick.outcomeId === pick.outcomeId,
      );

      const isExistingMarket = state.picks.some(
        (existingPick) => existingPick.marketId === pick.marketId,
      );

      // remove it
      if (isExistingPick) {
        const newPicks = state.picks.filter(
          (existingPick) =>
            !(
              existingPick.sourceId === pick.sourceId &&
              existingPick.outcomeId === pick.outcomeId
            ),
        );

        return {
          picks: newPicks,
          confirmContestId: null,
          pickAcceptedId: null,
          justAccepted: false,
        };
      }

      // replace it (market exists but different outcome)
      if (isExistingMarket) {
        const newPicks = state.picks.map((existingPick) =>
          existingPick.marketId === pick.marketId ? pickDetails : existingPick,
        );

        return {
          picks: newPicks,
          confirmContestId: null,
          pickAcceptedId: null,
          justAccepted: false,
        };
      }

      // add it
      return {
        picks: [...state.picks, pickDetails],
        confirmContestId: null,
        pickAcceptedId: null,
        justAccepted: false,
      };
    }),

  removePick: (marketId) =>
    set((state) => ({
      picks: state.picks.filter(
        (existingPick) => existingPick.marketId !== marketId,
      ),
      confirmContestId: null,
      pickAcceptedId: null,
    })),

  updatePickslip: (pickslip) =>
    set((state) => {
      let newPicks = state.picks;

      if (pickslip.picks) {
        newPicks = pickslip.picks.map((p) => {
          const old = state.picks.find(
            (x) => x.marketId === p.marketId && x.outcomeId === p.outcomeId,
          );

          return {
            ...(old ?? {}),
            ...p,
            competitor: p.competitor ?? old?.competitor,
          };
        });
      }

      return {
        ...state,
        ...pickslip,
        picks: newPicks,
      };
    }),

  reset: () => set(initialState),
}));

export { usePickslipStore };
export type { PickslipStore, PickslipActions, PickslipState };
